import { Typography, type TypographyProps } from "@mui/material";
import { type ReactElement } from "react";

import { type TitleVariant } from "../../../ui-theme";
import { mergeSxProps } from "../../lib/mergeSxProps";

export interface TitleProps extends Omit<
  TypographyProps,
  "component" | "variant" | "fontWeight" | "fontSize" | "lineHeight"
> {
  variant?: TitleVariant;
  /**
   * Title components must define a document hierarchy to meet WCAG 2.1 accessibility guidelines.
   * See: https://www.notion.so/BP-Title-and-Text-Document-Hierarchy-5931375db62f464a8169de791f82c9da
   */
  component: TitleVariant;
  bold?: boolean;
  semibold?: boolean;
}

/**
 * WCAG 2.1 compliant Title component.
 * ---
 *
 * In order to meet WCAG 2.1 accessibility guidelines, you ***must*** define
 * the `component` prop, to guide you to manage your document hierarchy.
 *
 * If you use the Title component, you must create a document hierarchy that ensures
 * `h1` -> `h2` -> `h3` -> `h4` -> `h5` -> `h6` structure.
 *
 * ***IMPORTANT***: You can never specify an `h4` without an `h1`, `h2`, `h3`, `h5`, or `h6` parent.
 *
 * If you do not have a document hierarchy, and are only defining style, then use the `Text` component
 * with your desired variant.
 *
 * See: https://www.notion.so/BP-Title-and-Text-Document-Hierarchy-5931375db62f464a8169de791f82c9da
 */
export function Title(props: TitleProps): ReactElement {
  const { bold, semibold, sx, variant, ...otherProps } = props;
  return (
    <Typography
      variant={variant ?? props.component}
      {...otherProps}
      sx={mergeSxProps(
        {
          fontWeight: (theme) => {
            if (bold) {
              return theme.typography.fontWeightBold;
            }

            if (semibold) {
              return theme.typography.fontWeightMedium;
            }

            return theme.typography.fontWeightRegular;
          },
        },
        sx
      )}
    />
  );
}
